import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { message, Table } from "antd";

import "./price.less";

import priceBg from "@assets/images/official_website_new/price_bg.jpg";
import { SCROLL_BRAND_IMAGES } from "../../shared/image_assets";
import { detectDevice } from "../../shared/detectDevice";
import Iconfont from "@components/iconfont/iconfont";
import OverlayScrollbar from "@components/overlay_scrollbar";
import { queryByConCloud } from "@api/cloud_disk";
import { queryMyManageTeams } from "@api/team";
import { getCookie } from "@util/utils";
import ManageTeamSelectModal from "./ManageTeamSelectModal";
import SubscribePackageErrorModal from "./SubscribePackageErrorModal";

function ComparisonValue({ value }) {
  if (value === true) {
    return (
      <Iconfont
        className="price-comparison-table__check"
        type="icon-a-dagouxi"
        aria-label="支持"
      />
    );
  }

  const displayValue = value == null || value === false ? "—" : value;

  return (
    <span
      className="price-comparison-table__value"
      title={displayValue === "—" ? undefined : String(displayValue)}
    >
      {displayValue}
    </span>
  );
}

function buildPlanComparisonTable(packageList) {
  const rowMap = new Map();

  packageList.forEach((plan, planIndex) => {
    const dataIndex = `plan_${planIndex}`;

    (plan.rightConfigs || []).forEach((rightConfig) => {
      if (!rightConfig?.rightId) return;

      if (!rowMap.has(rightConfig.rightId)) {
        rowMap.set(rightConfig.rightId, {
          key: rightConfig.rightId,
          feature: rightConfig.name,
        });
      }

      const compareDescription = rightConfig.compareDescription;
      const hasCompareDescription =
        compareDescription != null && String(compareDescription).trim() !== "";

      rowMap.get(rightConfig.rightId)[dataIndex] =
        hasCompareDescription
          ? compareDescription
          : rightConfig.type === "BOOLEAN"
            ? rightConfig.value === true
            : null;
    });
  });

  return {
    columns: [
      {
        title: "套餐名称",
        dataIndex: "feature",
        key: "feature",
        fixed: "left",
        width: "20%",
        ellipsis: true,
      },
      ...packageList.map((plan, planIndex) => {
        const dataIndex = `plan_${planIndex}`;
        return {
          title: plan.name,
          dataIndex,
          key: plan.id || dataIndex,
          ellipsis: true,
          render: (value) => <ComparisonValue value={value} />,
        };
      }),
    ],
    dataSource: Array.from(rowMap.values()),
  };
}

function PlanColumn({ plan, isFree, onSubscribe, subscribeDisabled }) {
  const benefits = (plan.rightConfigs || []).filter(({ description }) => description);
  const hasPrice = plan.price != null && plan.price !== "";
  const hasDisplayPrice = plan.displayPrice != null && plan.displayPrice !== "";

  return (
    <div className="price-plan">
      <h3 className="price-plan__title">{plan.name}</h3>
      <ul className="price-plan__features">
        {benefits.map((benefit, index) => (
          <li
            key={benefit.rightId || `${plan.id}-benefit-${index}`}
            className="price-plan__feature"
          >
            <Iconfont
              className="price-plan__feature-icon"
              type="icon-a-dagouxi"
              aria-hidden="true"
            />
            <span className="price-plan__feature-text">{benefit.description}</span>
          </li>
        ))}
      </ul>
      <div className="price-plan__price-cell">
        <div className="price-plan__price-line">
          <span className="price-plan__price-main">
            {isFree ? "免费" : hasPrice ? `¥${plan.price}` : "--"}
          </span>
          {!isFree && hasPrice && (
            <span className="price-plan__price-suffix">/年起</span>
          )}
          {!isFree && hasDisplayPrice && (
            <span className="price-plan__original-price">
              <span>¥</span>
              <span className="with-line">{plan.displayPrice}</span>
              <span>/年起</span>
            </span>
          )}
        </div>
        {/* 免费卡的「核心基础服务」只作为套餐说明标签：保留按钮外观与 hover/按下反馈，
            点击不再触发任何跳转（原先是跳私域链接）。 */}
        <button
          type="button"
          className={`price-plan__button${isFree ? " price-plan__button--solo" : ""}`}
          disabled={!isFree && subscribeDisabled}
          onClick={isFree ? undefined : (e) => onSubscribe?.(e, plan)}
        >
          {isFree ? "核心基础服务" : "立即订阅"}
        </button>
      </div>
    </div>
  );
}

export default function PricePage({ onSubscribe }) {
  const [plans, setPlans] = useState([]);
  const isPad = detectDevice() === "pad";
  const [fixedHeader, setFixedHeader] = useState({
    visible: false,
    top: 0,
    left: 0,
    width: 0,
  });
  const plansScrollContainerRef = useRef(null);
  const plansScrollTargetRef = useRef(null);
  const comparisonTableRef = useRef(null);
  const fixedHeaderRef = useRef(null);
  const subscribeRequestIdRef = useRef(0);
  const [manageTeams, setManageTeams] = useState([]);
  const [selectedSubscribePlan, setSelectedSubscribePlan] = useState(null);
  const [emptyTeamVisible, setEmptyTeamVisible] = useState(false);
  const [teamSelectVisible, setTeamSelectVisible] = useState(false);
  const [subscribeLoadingPlanId, setSubscribeLoadingPlanId] = useState();

  useEffect(() => {
    let active = true;

    queryByConCloud({})
      .then((res) => {
        if (active && res?.success) {
          setPlans(Array.isArray(res.result) ? res.result : []);
        }
      })
      .catch(() => {
        if (active) setPlans([]);
      });

    return () => {
      active = false;
    };
  }, []);

  const handleSubscribe = (e, plan) => {
    e.preventDefault();
    if (!plan?.id) {
      if (typeof onSubscribe === "function") onSubscribe(e, plan);
      return;
    }
    if (subscribeLoadingPlanId) return;

    const redirectToLogin = () => {
      window.location.href = "/login.html";
    };

    if (!getCookie("mtoken")) {
      redirectToLogin();
      return;
    }

    const requestId = subscribeRequestIdRef.current + 1;
    subscribeRequestIdRef.current = requestId;
    setSelectedSubscribePlan(plan);
    setTeamSelectVisible(false);
    setEmptyTeamVisible(false);
    setSubscribeLoadingPlanId(plan.id);

    queryMyManageTeams()
      .then((res) => {
        console.log("queryMyManageTeams", res);
        if (requestId !== subscribeRequestIdRef.current) return;

        setManageTeams(res?.result || []);
        console.log("queryMyManageTeams res?.result", res?.result);
        if (res?.result?.length) {
          setTeamSelectVisible(true);
        } else {
          console.log("11111111111vvv")
          setEmptyTeamVisible(true);
        }
      })
      .catch((error) => {
        if (requestId === subscribeRequestIdRef.current) {
          if (error?.status === 401) {
            redirectToLogin();
            return;
          }
          message.error("网络异常，请重试");
        }
      })
      .finally(() => {
        if (requestId === subscribeRequestIdRef.current) {
          setSubscribeLoadingPlanId();
        }
      });
  };

  const handleSubscribeModalCancel = () => {
    console.log("handleSubscribeModalCancel");
    setTeamSelectVisible(false);
    setEmptyTeamVisible(false);
  };

  const handleCreateTeam = () => {
    setEmptyTeamVisible(false);
    window.location.href = "/team_create.html?hadTeam=1";
  };

  const handleSelectManageTeam = (team) => {
    if (!team?.id || !selectedSubscribePlan?.id) return;

    const params = new URLSearchParams({
      clientId: team.id,
      selectTab: "9",
      cloudProductId: selectedSubscribePlan.id,
    });
    window.location.href = `/team_cloud.html/#/cloud?${params.toString()}`;
  };
  /** 跨标签跳转：先让 :active/:focus 的视觉变化提交一帧，再 window.open；
   *  避免部分 Pad 在 touchend 立刻打开新标签，导致点击反馈到回原页才看到。 */
  const handleSurveyOpen = (e) => {
    e.preventDefault();
    const target = e.currentTarget;
    target.classList.add("is-pressed");
    requestAnimationFrame(() => requestAnimationFrame(() => {
      window.open("https://v.wjx.cn/vm/PD6aaEY.aspx#", "_blank", "noopener,noreferrer");
      setTimeout(() => target.classList.remove("is-pressed"), 200);
    }));
  };

  const freePlan = plans.find(({ packageType }) => packageType === "FREE");
  const paidPlans = plans.filter(({ packageType }) => packageType !== "FREE");
  const comparisonTable = useMemo(
    () => buildPlanComparisonTable(plans),
    [plans]
  );

  useEffect(() => {
    if (!plans.length) return undefined;

    let frameId = null;
    const updateFixedHeader = () => {
      frameId = null;
      const tableWrap = comparisonTableRef.current;
      const tableHeader = tableWrap?.querySelector(
        ".price-comparison-table__inner .ant-table-thead"
      );
      if (!tableWrap || !tableHeader) return;

      const tableRect = tableWrap.getBoundingClientRect();
      const headerHeight = tableHeader.getBoundingClientRect().height;
      const navBottom = document
        .getElementById("nav")
        ?.getBoundingClientRect().bottom || 0;
      const next = {
        visible:
          tableRect.top <= navBottom &&
          tableRect.bottom > navBottom + headerHeight,
        top: navBottom,
        left: tableRect.left,
        width: tableRect.width,
      };

      setFixedHeader((current) => {
        if (
          current.visible === next.visible &&
          current.top === next.top &&
          current.left === next.left &&
          current.width === next.width
        ) {
          return current;
        }
        return next;
      });
    };
    const scheduleUpdate = () => {
      if (frameId != null) return;
      frameId = requestAnimationFrame(updateFixedHeader);
    };

    updateFixedHeader();
    document.addEventListener("scroll", scheduleUpdate, {
      capture: true,
      passive: true,
    });
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);

    const resizeObserver = typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(scheduleUpdate);
    if (resizeObserver) {
      resizeObserver.observe(comparisonTableRef.current);
      const nav = document.getElementById("nav");
      if (nav) resizeObserver.observe(nav);
    }

    return () => {
      if (frameId != null) cancelAnimationFrame(frameId);
      document.removeEventListener("scroll", scheduleUpdate, true);
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      resizeObserver?.disconnect();
    };
  }, [plans.length]);

  useEffect(() => {
    if (!fixedHeader.visible) return undefined;

    const source = comparisonTableRef.current?.querySelector(
      ".price-comparison-table__inner .ant-table-content"
    );
    const target = fixedHeaderRef.current?.querySelector(".ant-table-content");
    if (!source || !target) return undefined;

    const syncScrollLeft = () => {
      target.scrollLeft = source.scrollLeft;
    };
    syncScrollLeft();
    source.addEventListener("scroll", syncScrollLeft, { passive: true });
    return () => source.removeEventListener("scroll", syncScrollLeft);
  }, [fixedHeader.visible, comparisonTable.columns.length]);

  return (
    <div className="price-page">
      {/* —— Hero（节点 79:12260，1920×679，背景图） —— */}
      <section
        className="price-hero"
        style={{ backgroundImage: `url("${priceBg}")` }}
      >
        <div className="price-hero__title">快速搭建您的专属美学型私域空间</div>
        <div className="price-hero__sub">
          <div className="price-hero__tag">
            <span className="price-hero__tag-text">零代码搭建品牌网站（个人/企业）</span>
          </div>
          <div className="price-hero__tag">
            <span className="price-hero__tag-text">快速生成高定作品集/产品册</span>
          </div>
          <div className="price-hero__tag">
            <span className="price-hero__tag-text">私域圈新商业工具</span>
          </div>
        </div>
        <a
          href="https://v.wjx.cn/vm/PD6aaEY.aspx#"
          className="price-hero__cta"
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleSurveyOpen}
        >
          立刻获取方案
        </a>
      </section>

      {/* —— 套餐区（节点 79:12267 Group 49088）：免费卡 + 三档合并卡 —— */}
      <section className="price-plans">
        <div className="price-plans__cards">
          {freePlan && (
            <article className="price-plan-card price-plan-card--solo">
              <PlanColumn
                plan={freePlan}
                isFree
              />
            </article>
          )}

          {paidPlans.length > 0 && (
            <article
              className="price-plan-card price-plan-card--merged"
              ref={plansScrollContainerRef}
            >
              <div
                className={`price-plans-row overlay-scrollbar-hidden${paidPlans.length > 3 ? " price-plans-row--overflow" : ""}`}
                ref={plansScrollTargetRef}
              >
                {paidPlans.map((plan, index) => (
                  <React.Fragment key={plan.id || `${plan.packageType}-${index}`}>
                    {index > 0 && (
                      <div
                        className="price-plan-divider"
                        style={{ "--price-plan-divider-index": index }}
                        aria-hidden="true"
                      />
                    )}
                    <PlanColumn
                      plan={plan}
                      isFree={false}
                      onSubscribe={handleSubscribe}
                      subscribeDisabled={Boolean(subscribeLoadingPlanId)}
                    />
                  </React.Fragment>
                ))}
              </div>
              {paidPlans.length > 3 && (
                <OverlayScrollbar
                  key={paidPlans.map(({ id }, index) => id || index).join("-")}
                  containerRef={plansScrollContainerRef}
                  targetRef={plansScrollTargetRef}
                  direction="horizontal"
                  size={4}
                  gap={6}
                  horizontalInsetStart={isPad ? 24 : 32}
                  horizontalInsetEnd={isPad ? 24 : 32}
                  background="#DBDBDB"
                  hoverSelector=".price-plans-row"
                  className="price-plans-scrollbar"
                />
              )}
            </article>
          )}
        </div>

        {plans.length > 0 && (
          <div className="price-comparison-table" ref={comparisonTableRef}>
            <Table
              bordered
              className="price-comparison-table__inner"
              columns={comparisonTable.columns}
              dataSource={comparisonTable.dataSource}
              pagination={false}
              rowKey="key"
              scroll={{ x: 768 }}
            />
            <OverlayScrollbar
              containerRef={comparisonTableRef}
              selector=".ant-table-content"
              direction="horizontal"
              size={4}
              gap={2}
            />
          </div>
        )}
      </section>

      {fixedHeader.visible && createPortal(
        <div
          ref={fixedHeaderRef}
          className="price-comparison-table price-comparison-table__fixed-header"
          style={{
            top: fixedHeader.top,
            left: fixedHeader.left,
            width: fixedHeader.width,
          }}
          aria-hidden="true"
        >
          <Table
            bordered
            className="price-comparison-table__inner price-comparison-table__fixed-header-table"
            columns={comparisonTable.columns}
            dataSource={[]}
            pagination={false}
            rowKey="key"
            scroll={{ x: 768 }}
            locale={{ emptyText: null }}
          />
        </div>,
        document.body
      )}

      {emptyTeamVisible && <SubscribePackageErrorModal
        emptyVisible={emptyTeamVisible}
        onCancel={handleSubscribeModalCancel}
        onCreateTeam={handleCreateTeam}
      />}
      {teamSelectVisible && <ManageTeamSelectModal
        teamVisible={teamSelectVisible}
        teamList={manageTeams}
        onCancel={handleSubscribeModalCancel}
        onSelectTeam={handleSelectManageTeam}
      />}

      {/* —— 联系我们（节点 79:12632） —— */}
      <section className="price-contact">
        <a
          href="https://v.wjx.cn/vm/PD6aaEY.aspx#"
          className="price-plan__cta price-plan__cta--contact"
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleSurveyOpen}
        >
          联系我们
        </a>
      </section>

      {/* —— LogoSection（节点 79:12634） —— */}
      <div className="price-logos-bg">
        <section className="price-logos">
          <div className="price-logos__title">10000+个⼈、3500+团队/企业⽤户正在使⽤不觉</div>
          <div className="price-logos__track">
            {[...SCROLL_BRAND_IMAGES, ...SCROLL_BRAND_IMAGES].map((src, i) => (
              <div key={`brand-${i}`} className="price-logos__item">
                <img src={typeof src === "string" ? src : src?.default} alt="" draggable={false} />
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
